import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import Timeline from "react-calendar-timeline";
import moment from "moment";
import "react-calendar-timeline/dist/Timeline.scss";
import FormSelect from "../../../Lib/FormSelect";
import { useDispatch, useSelector } from "react-redux";
import {
  FETCH_RESERVATION_REQUEST,
  FETCH_ROOM_REQUEST,
  GET_ALL_RESERVATION_LIST_REQUEST,
  GET_ROOM_NUMBER_DETAIL_REQUEST,
  GET_ROOM_STATUS_REQUEST,
  GET_ROOM_TYPE_NAME_REQUEST,
  PUT_ROOM_REASSIGN_REQUEST,
} from "../../../Utils/Constant";
import {
  colorMap,
  getStartAndEndDate,
  occupancyOptions,
} from "./Components/frontDeskItem";
import * as Popover from "@radix-ui/react-popover";
import { useLocation, useNavigate } from "react-router-dom";
import { hasAddAccess, hasEditAccess } from "../../../Common/CheckAccess";
import "./FrontDeskDashboard.css";
import { UserCheck } from "lucide-react";

const months = moment.months();

const FrontDeskDashboard = React.memo(() => {
  const dispatch = useDispatch();
  const today = moment().startOf("day");

  const currentYear = moment().year();
  const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
  const filterRef = useRef(null);
  const timelineRef = useRef(null);
  const timelineContainerRef = useRef(null);

  // Basic state
  const [selectedYear, setSelectedYear] = useState(today.year());
  const [selectedMonth, setSelectedMonth] = useState(today.month());
  const [selectedDate, setSelectedDate] = useState(moment().startOf("month"));
  const [filterRoomType, setFilterRoomType] = useState("");
  const [filteroccupancy, setFilteroccupancy] = useState("");
  const [filterStatus, setfilterStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Redux state
  const { roomTypeName, bookingstatus } = useSelector(
    (state) => state.roomType
  );
  const { rooms, reserations, availability, loading, error, roomDetail } =
    useSelector((state) => state.frontdesk);

  const [roomTypesName, setRoomTypesName] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingStatus, setBookingStatus] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [permissions, setPermissions] = useState([]);
  const existingPermissions = localStorage.getItem("checkPermission");

  useEffect(() => {
    if (existingPermissions != undefined) {
      const parsedData = JSON.parse(existingPermissions);
      setPermissions(parsedData);
    }
  }, [existingPermissions]);

  useEffect(()=>{
    dispatch({ type: FETCH_ROOM_REQUEST });
  },[])

  // Enhanced drag selection state - Fixed implementation
  const [dragSelection, setDragSelection] = useState({
    isActive: false,
    isDragging: false,
    startDate: null,
    endDate: null,
    roomId: null,
    roomNumber: null,
    roomType: null,
    startX: 0,
    endX: 0,
    rowY: 0,
    rowHeight: 55,
  });

  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [finalSelection, setFinalSelection] = useState({
    startDate: null,
    endDate: null,
    roomId: null,
    roomNumber: null,
    roomType: null,
    duration: 0,
  });

  // Timeline dimensions
  const [timelineDimensions, setTimelineDimensions] = useState({
    width: 0,
    sidebarWidth: 200,
    contentWidth: 0,
    rowHeight: 55,
    headerHeight: 60,
  });

  // Mouse tracking for drag
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMouseDown, setIsMouseDown] = useState(false);

  // Popup state with enhanced positioning
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [roomPopupPos, setRoomPopupPos] = useState({
    x: 0,
    y: 0,
    position: "right",
    maxHeight: "auto",
  });
  const roomPopupRef = useRef(null);
  const dropdownRef = useRef(null);

  // Memoized values for performance optimization
  const visibleTimeStart = useMemo(
    () => selectedDate.valueOf(),
    [selectedDate]
  );
  const visibleTimeEnd = useMemo(
    () => moment(selectedDate).clone().endOf("month").valueOf(),
    [selectedDate]
  );

  // Smart popup positioning function - memoized
  const calculateOptimalPosition = useCallback(
    (targetRect, popupWidth = 320, popupHeight = 400) => {
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
        scrollX: window.scrollX,
        scrollY: window.scrollY,
      };

      const spacing = 10; // Minimum spacing from edges

      // Calculate available space in each direction
      const spaceRight = viewport.width - (targetRect.right + spacing);
      const spaceLeft = targetRect.left - spacing;
      const spaceBottom = viewport.height - (targetRect.bottom + spacing);
      const spaceTop = targetRect.top - spacing;

      let position = "right";
      let x = targetRect.right + spacing;
      let y = targetRect.top;
      let maxHeight = "auto";

      // Determine best horizontal position
      if (spaceRight >= popupWidth) {
        // Enough space on the right
        position = "right";
        x = targetRect.right + spacing;
      } else if (spaceLeft >= popupWidth) {
        // Not enough space on right, try left
        position = "left";
        x = targetRect.left - popupWidth - spacing;
      } else {
        // Not enough space on either side, use the side with more space
        if (spaceRight > spaceLeft) {
          position = "right";
          x = targetRect.right + spacing;
          // Adjust width if needed
          popupWidth = Math.min(popupWidth, spaceRight - spacing);
        } else {
          position = "left";
          x = Math.max(spacing, targetRect.left - popupWidth - spacing);
          popupWidth = Math.min(popupWidth, spaceLeft - spacing);
        }
      }

      // Adjust vertical position to keep popup in viewport
      if (y + popupHeight > viewport.height - spacing) {
        // Popup would go below viewport, adjust upward
        y = Math.max(spacing, viewport.height - popupHeight - spacing);
        maxHeight = viewport.height - spacing * 2;
      }

      // Ensure popup doesn't go above viewport
      if (y < spacing) {
        y = spacing;
        maxHeight = viewport.height - spacing * 2;
      }

      // If popup is too tall for viewport, set max height and enable scrolling
      if (popupHeight > viewport.height - spacing * 2) {
        maxHeight = viewport.height - spacing * 2;
      }

      return {
        x: Math.max(
          spacing,
          Math.min(x, viewport.width - popupWidth - spacing)
        ),
        y: Math.max(spacing, y),
        position,
        maxHeight,
        width: popupWidth,
      };
    },
    []
  );

  // Memoized room click handler
  const handleRoomClick = useCallback(
    (e, room) => {
      e.stopPropagation();
      const rect = e.target.getBoundingClientRect();

      // Calculate optimal position for the popup
      const optimalPos = calculateOptimalPosition(rect, 320, 450);

      setRoomPopupPos(optimalPos);
      setSelectedRoom(room);
      dispatch({
        type: GET_ROOM_NUMBER_DETAIL_REQUEST,
        payload: { id: room.id },
      });
      setOpenDialog(true);
    },
    [calculateOptimalPosition, dispatch]
  );

  // Enhanced booking popup positioning - memoized
  const handleBookingClick = useCallback(
    (booking, targetRect) => {
      const optimalPos = calculateOptimalPosition(targetRect, 350, 300);

      setSelectedBooking(booking);
     
      setDropdownPosition({
        top: optimalPos.y,
        left: optimalPos.x,
        position: optimalPos.position,
        maxHeight: optimalPos.maxHeight,
      });
    },
    [calculateOptimalPosition]
  );

  // Memoized fetch data function
  const fetchData = useCallback(() => {
    const { start, end } = getStartAndEndDate(selectedYear, selectedMonth);
    dispatch({
      type: FETCH_RESERVATION_REQUEST,
      payload: {
        start,
        end,
        booking_status:
          filterStatus && filterStatus !== "all" ? filterStatus : undefined,
      },
    });
    dispatch({
      type: FETCH_ROOM_REQUEST,
      payload: {
        capacity:
          filteroccupancy && filteroccupancy !== "all"
            ? filteroccupancy
            : undefined,
      },
    });
    dispatch({ type: GET_ROOM_TYPE_NAME_REQUEST });
    dispatch({ type: GET_ROOM_STATUS_REQUEST });
  }, [dispatch, selectedYear, selectedMonth, filterStatus, filteroccupancy]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if(location?.search==="?from=reservation"){
      fetchData();
    }
  }, [location?.search]);

  // Memoized room types transformation
  const transformedRoomTypes = useMemo(() => {
    if (!roomTypeName?.data?.length) return [];
    return [
      { label: "All", value: "all" },
      ...roomTypeName.data.map((rt) => ({
        label: rt.room_type,
        value: rt.id,
      })),
    ];
  }, [roomTypeName]);

  useEffect(() => {
    setRoomTypesName(transformedRoomTypes);
  }, [transformedRoomTypes]);

  // Memoized booking status transformation
  const transformedBookingStatus = useMemo(() => {
    const bookingStatusObj = bookingstatus?.data?.find(
      (item) => item.key === "RoomBookingStatus"
    );
    if (!bookingStatusObj) return [];

    return [
      { label: "All", value: "all" },
      ...Object.entries(bookingStatusObj.value).map(([key, value]) => ({
        label: key
          .replaceAll("_", " ")
          .toLowerCase()
          .replace(/^\w/, (c) => c.toUpperCase()),
        value,
      })),
    ];
  }, [bookingstatus]);

  useEffect(() => {
    setBookingStatus(transformedBookingStatus);
  }, [transformedBookingStatus]);

  // Memoized timeline groups generation
  const generateTimelineGroups = useCallback(() => {
    if (!rooms?.data?.length) return [];

    const groupList = [];

    rooms.data.forEach((roomType, index) => {
      if (
        filterRoomType &&
        filterRoomType !== "all" &&
        roomType.id !== filterRoomType
      ) {
        return;
      }

      const matchingRooms = roomType.room.filter((room) => {
        if (filteroccupancy && filteroccupancy !== "all") {
          const roomCapacity =
            room.capacity?.toString() || roomType.capacity?.toString();
          return roomCapacity === filteroccupancy;
        }
        return true;
      });

      if (matchingRooms.length > 0) {
        const parentId = 1000 + index;

        // FIXED: Mark room type rows as completely non-interactive
        groupList.push({
          id: parentId,
          title: roomType.room_type,
          groupOnly: true,
          roomTypeId: roomType.id,
          roomTypeName: roomType.room_type,
          isParent: true,
          isRoomType: true,
          isDraggable: false,
          isSelectable: false,
          canClick: false,
          isDisabled: true, // NEW: Explicit disabled flag
        });

        matchingRooms.forEach((room) => {
          const roomWithType = {
            ...room,
            roomTypeId: roomType.id,
            roomTypeName: roomType.room_type,
          };

          groupList.push({
            id: room.id,
            title: (
              <span
                className="text-primary fw-semibold d-flex align-items-center room-clickable"
                style={{
                  cursor: "pointer",
                  position: "relative",
                  zIndex: 1000,
                  pointerEvents: "auto",
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRoomClick(e, roomWithType);
                }}
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent drag from starting on room number
                }}
              >
                <i className="bi bi-house-door me-2"></i>
                {room.room_number}
              </span>
            ),
            roomNumber: room.room_number,
            parent: parentId,
            capacity: room.capacity || roomType.capacity,
            roomTypeId: roomType.id,
            roomTypeName: roomType.room_type,
            isRoom: true,
            isRoomType: false,
            isDraggable: true,
            isSelectable: true,
            isDisabled: false, // NEW: Explicit enabled flag
          });
        });
      }
    });

    return groupList;
  }, [rooms, filterRoomType, filteroccupancy, handleRoomClick]);

  // UPDATED: Enhanced timeline items generation to handle multiple rooms per reservation
  const generateTimelineItems = useCallback(
    (groups) => {
      if (!reserations?.data?.length || !groups.length) return [];

      // Create a map of room IDs to group information
      const roomIdToGroupMap = {};
      groups.forEach((group) => {
        if (group.roomNumber) {
          roomIdToGroupMap[group.id] = {
            groupId: group.id,
            roomTypeId: group.roomTypeId,
            capacity: group.capacity,
          };
        }
      });

      const timelineItems = [];

      reserations.data.forEach((reservation) => {
        // Filter by booking status if specified
        if (
          filterStatus &&
          filterStatus !== "all" &&
          reservation.booking_status !== filterStatus
        ) {
          return;
        }

        // Handle multiple rooms per reservation
        if (reservation.rooms && Array.isArray(reservation.rooms)) {
          reservation.rooms.forEach((roomInfo, roomIndex) => {
            const roomGroup = roomIdToGroupMap[roomInfo.room_id];

            if (!roomGroup) {
              console.warn(`Room ${roomInfo.room_id} not found in groups`);
              return;
            }

            // Create a unique item ID for each room in the reservation
            const itemId = `${reservation.booking_id}-room-${roomIndex}`;

            // Determine the title based on number of rooms
            let itemTitle = `${reservation.guest.first_name} ${reservation.guest.last_name}`;
            if (reservation.rooms.length > 1) {
              itemTitle += ` (${roomIndex + 1}/${reservation.rooms.length})`;
            }

            timelineItems.push({
              id: itemId,
              group: roomGroup.groupId,
              title: itemTitle,
              start_time: moment(reservation.check_in_date_time),
              end_time: moment(reservation.check_out_date_time),
              booking_status: reservation.booking_status,
              bookingData: {
                ...reservation,
                currentRoomInfo: roomInfo, // Add current room info for this specific item
                roomIndex: roomIndex, // Add room index for reference
              },
              canMove: false,
              canResize: false,
              canSelect: true,
              itemProps: {
                style: {
                  borderRadius: "8px",
                  borderColor: "rgba(0,0,0,0.1)",
                  background: colorMap[reservation.booking_status] || "#e3f2fd",
                  color: "#000",
                  fontSize: "12px",
                  fontWeight: "500",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  border:
                    reservation.rooms.length > 1
                      ? "2px solid #007bff"
                      : "1px solid rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  // Add visual indicator for multi-room reservations
                  position: "relative",
                },
                onMouseEnter: (e) => {
                  e.currentTarget.style.transform = "scale(1.02)";
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)";
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                },
              },
            });
          });
        } else {
          // Fallback for old single room format (if any exist)
          console.warn(
            "Old single room format detected for reservation:",
            reservation.id
          );
        }
      });

      return timelineItems;
    },
    [reserations, filterStatus]
  );

  // Memoized timeline groups and items - moved after function definitions
  const memoizedTimelineGroups = useMemo(
    () => generateTimelineGroups(),
    [generateTimelineGroups]
  );
  const memoizedTimelineItems = useMemo(
    () => generateTimelineItems(memoizedTimelineGroups),
    [generateTimelineItems, memoizedTimelineGroups]
  );

  // Enhanced date calculation functions - memoized
  const calculateDateFromX = useCallback(
    (x) => {
      const { contentWidth, sidebarWidth } = timelineDimensions;
      if (contentWidth <= 0) return moment();

      const timeRange = visibleTimeEnd - visibleTimeStart;
      const relativeX = Math.max(0, x - sidebarWidth);
      const timeRatio = relativeX / contentWidth;
      const calculatedTime = visibleTimeStart + timeRange * timeRatio;

      return moment(calculatedTime).startOf("day");
    },
    [timelineDimensions, visibleTimeStart, visibleTimeEnd]
  );

  const calculateXFromDate = useCallback(
    (date) => {
      const { contentWidth, sidebarWidth } = timelineDimensions;
      if (contentWidth <= 0) return sidebarWidth;

      const timeRange = visibleTimeEnd - visibleTimeStart;
      const timeOffset = date.valueOf() - visibleTimeStart;
      const timeRatio = timeOffset / timeRange;

      return sidebarWidth + contentWidth * timeRatio;
    },
    [timelineDimensions, visibleTimeStart, visibleTimeEnd]
  );

  // FIXED: Get room from Y position - completely exclude disabled room type rows
  const getRoomFromY = useCallback(
    (y) => {
      const { rowHeight, headerHeight } = timelineDimensions;

      // Calculate which visual row was clicked
      const visualRowIndex = Math.floor((y - headerHeight) / rowHeight);

      // Get all groups in display order
      const allGroups = memoizedTimelineGroups;

      // Check if the clicked row corresponds to a disabled room type
      if (visualRowIndex >= 0 && visualRowIndex < allGroups.length) {
        const clickedGroup = allGroups[visualRowIndex];

        // If it's a room type row (disabled), return null immediately
        if (
          clickedGroup.isRoomType ||
          clickedGroup.isDisabled ||
          !clickedGroup.isDraggable
        ) {
          return null;
        }

        // If it's a valid room number row, return it
        if (
          clickedGroup.isRoom &&
          clickedGroup.roomNumber &&
          clickedGroup.isDraggable
        ) {
          return { room: clickedGroup, index: visualRowIndex };
        }
      }

      return null;
    },
    [timelineDimensions, memoizedTimelineGroups]
  );

  // FIXED: Enhanced drag handlers that completely prevent interaction with room type rows
  const handleTimelineMouseDown = useCallback(
    (e) => {
      // Only handle left mouse button
      if (e.button !== 0) return;

      // Check if clicking on a room number (has room-clickable class)
      if (e.target.closest(".room-clickable")) {
        return; // Don't start drag when clicking room numbers
      }

      // Check if clicking on a timeline item (booking)
      if (e.target.closest(".rct-item")) {
        return; // Don't start drag when clicking on bookings
      }

      // FIXED: Check if clicking on disabled elements
      if (
        e.target.closest(".room-type-disabled") ||
        e.target.closest(".non-selectable-row") ||
        e.target.closest(".disabled-row")
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }

      if (!timelineContainerRef.current) return;

      const rect = timelineContainerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if click is in timeline content area (not sidebar)
      if (x < timelineDimensions.sidebarWidth) return;

      // FIXED: Get room information - this now completely excludes room type rows
      const roomInfo = getRoomFromY(y);
      if (!roomInfo) {
        return; // Exit if no valid room (clicked on room type row or invalid area)
      }

      const { room, index } = roomInfo;

      // FIXED: Triple check to ensure we only work with valid room number rows
      if (
        room.isRoomType ||
        room.isDisabled ||
        !room.isDraggable ||
        !room.isSelectable
      ) {
        return;
      }

      // Prevent default to avoid text selection
      e.preventDefault();
      e.stopPropagation();

      const startDate = calculateDateFromX(x);
      // BLOCK: Prevent drag selection on past dates
      if (startDate.isBefore(today, "day")) {
        // Optionally, show a toast/alert here
        return;
      }

      // Calculate row position for visual feedback
      const rowY =
        timelineDimensions.headerHeight + index * timelineDimensions.rowHeight;
      setIsMouseDown(true);
      setMousePosition({ x, y });

      setDragSelection({
        isActive: true,
        isDragging: true,
        startDate: startDate.clone(),
        endDate: startDate.clone(),
        roomId: room.id,
        roomNumber: room.roomNumber,
        roomType: room.roomTypeName,
        startX: x,
        endX: x,
        rowY,
        rowHeight: timelineDimensions.rowHeight,
      });
    },
    [
      timelineContainerRef,
      timelineDimensions,
      getRoomFromY,
      calculateDateFromX,
      today,
    ]
  );

  const handleTimelineMouseMove = useCallback(
    (e) => {
      if (
        !isMouseDown ||
        !dragSelection.isDragging ||
        !timelineContainerRef.current
      )
        return;

      const rect = timelineContainerRef.current.getBoundingClientRect();
      const x = Math.max(
        timelineDimensions.sidebarWidth,
        Math.min(rect.width, e.clientX - rect.left)
      );
      const y = e.clientY - rect.top;

      // Update mouse position
      setMousePosition({ x, y });

      // Calculate end date from current mouse position
      const endDate = calculateDateFromX(x);

      // Update drag state
      setDragSelection((prev) => ({
        ...prev,
        endDate: endDate.clone(),
        endX: x,
      }));
    },
    [
      isMouseDown,
      dragSelection.isDragging,
      dragSelection.startDate,
      timelineDimensions,
      calculateDateFromX,
    ]
  );

  const handleTimelineMouseUp = useCallback(() => {
    if (!isMouseDown || !dragSelection.isDragging) {
      setIsMouseDown(false);
      setDragSelection((prev) => ({ ...prev, isDragging: false }));
      return;
    }

    // Ensure proper date order and minimum duration
    const startDate = moment
      .min(dragSelection.startDate, dragSelection.endDate)
      .startOf("day");
    let endDate = moment
      .max(dragSelection.startDate, dragSelection.endDate)
      .startOf("day");

    // Ensure at least 1 night duration
    if (startDate.isSameOrAfter(endDate)) {
      endDate = startDate.clone().add(1, "day");
    } else {
      // Add 1 day to end date for checkout
      endDate = endDate.add(1, "day");
    }

    const duration = endDate.diff(startDate, "days");

    // Set final selection and show modal
    setFinalSelection({
      startDate: startDate.clone(),
      endDate: endDate.clone(),
      roomId: dragSelection.roomId,
      roomNumber: dragSelection.roomNumber,
      roomType: dragSelection.roomType,
      duration,
    });
    setShowDateRangeModal(true);

    // Clean up drag state
    setIsMouseDown(false);
    setDragSelection({
      isActive: false,
      isDragging: false,
      startDate: null,
      endDate: null,
      roomId: null,
      roomNumber: null,
      roomType: null,
      startX: 0,
      endX: 0,
      rowY: 0,
      rowHeight: 55,
    });
  }, [isMouseDown, dragSelection]);

  // Global mouse event handlers
  useEffect(() => {
    if (isMouseDown) {
      document.addEventListener("mousemove", handleTimelineMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleTimelineMouseUp, {
        passive: false,
      });

      return () => {
        document.removeEventListener("mousemove", handleTimelineMouseMove);
        document.removeEventListener("mouseup", handleTimelineMouseUp);
      };
    }
  }, [isMouseDown, handleTimelineMouseMove, handleTimelineMouseUp]);

  // Prevent context menu during drag
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (dragSelection.isDragging) {
        e.preventDefault();
      }
    };

    document.addEventListener("contextmenu", handleContextMenu);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [dragSelection.isDragging]);

  // Update timeline dimensions - memoized
  const updateDimensions = useCallback(() => {
    if (timelineContainerRef.current) {
      const rect = timelineContainerRef.current.getBoundingClientRect();
      const sidebarWidth = 200;
      const contentWidth = rect.width - sidebarWidth;

      setTimelineDimensions((prev) => ({
        ...prev,
        width: rect.width,
        contentWidth,
        sidebarWidth,
      }));
    }
  }, []);

  useEffect(() => {
    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, [updateDimensions, memoizedTimelineGroups]);

  // Handle window resize to reposition popups - memoized
  const handleResize = useCallback(() => {
    if (openDialog || selectedBooking) {
      // Close popups on resize to avoid positioning issues
      setOpenDialog(false);
      setSelectedBooking(null);
    }
  }, [openDialog, selectedBooking]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  // Memoized navigation function
  const navigateToAddReservation = useCallback(() => {
    if (!finalSelection.roomId) return;

    const reservationData = {
      roomId: finalSelection.roomId,
      roomNumber: finalSelection.roomNumber,
      roomType: finalSelection.roomType,
      checkIn: finalSelection.startDate.format("YYYY-MM-DD"),
      checkOut: finalSelection.endDate.format("YYYY-MM-DD"),
      duration: finalSelection.duration,
    };

    navigate("/reservation/add-room-booking", { state: reservationData });

    // Reset selection state
    setShowDateRangeModal(false);
    setFinalSelection({
      startDate: null,
      endDate: null,
      roomId: null,
      roomNumber: null,
      roomType: null,
      duration: 0,
    });
  }, [finalSelection, navigate]);

  // Click outside handlers - memoized
  const handleFilterClickOutside = useCallback((event) => {
    if (filterRef.current && !filterRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  }, []);

  const handleBookingClickOutside = useCallback((event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setSelectedBooking(null);
    }
  }, []);

  const handleRoomClickOutside = useCallback((event) => {
    if (roomPopupRef.current && !roomPopupRef.current.contains(event.target)) {
      setSelectedRoom(null);
      setOpenDialog(false);
    }
  }, []);

  useEffect(() => {
    if (showFilters) {
      document.addEventListener("mousedown", handleFilterClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleFilterClickOutside);
    };
  }, [showFilters, handleFilterClickOutside]);

  useEffect(() => {
    document.addEventListener("mousedown", handleBookingClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleBookingClickOutside);
    };
  }, [handleBookingClickOutside]);

  useEffect(() => {
    document.addEventListener("mousedown", handleRoomClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleRoomClickOutside);
    };
  }, [handleRoomClickOutside]);

  // Memoized year options
  const yearOptions = useMemo(
    () => years.map((y) => ({ value: y, label: y })),
    [years]
  );

  // Memoized active filters count
  const getActiveFiltersCount = useCallback(() => {
    return [filterRoomType, filteroccupancy, filterStatus].filter(
      (f) => f && f !== "all"
    ).length;
  }, [filterRoomType, filteroccupancy, filterStatus]);

  // Enhanced selection overlay style with smooth animations - memoized
  const getSelectionOverlayStyle = useCallback(() => {
    if (!dragSelection.isDragging || !dragSelection.isActive)
      return { display: "none" };

    const startX = Math.min(dragSelection.startX, dragSelection.endX);
    const endX = Math.max(dragSelection.startX, dragSelection.endX);
    const width = Math.max(endX - startX, 10); // Minimum width

    return {
      position: "absolute",
      top: dragSelection.rowY,
      left: startX,
      width,
      height: dragSelection.rowHeight,
      background:
        "linear-gradient(135deg, rgba(0, 123, 255, 0.3) 0%, rgba(0, 123, 255, 0.5) 100%)",
      pointerEvents: "none",
      zIndex: 10,
      borderRadius: "6px",
      border: "2px solid #007bff",
      boxShadow: "0 4px 12px rgba(0, 123, 255, 0.3)",
      transition: "all 0.1s ease-out",
    };
  }, [dragSelection]);

  // Enhanced selection tooltip style - memoized
  const getSelectionTooltipStyle = useCallback(() => {
    if (!dragSelection.isDragging || !dragSelection.isActive)
      return { display: "none" };

    const startX = Math.min(dragSelection.startX, dragSelection.endX);
    const duration = Math.abs(
      dragSelection.endDate?.diff(dragSelection.startDate, "days") || 0
    );

    return {
      position: "absolute",
      top: dragSelection.rowY - 50,
      left: startX,
      fontSize: "12px",
      fontWeight: "600",
      pointerEvents: "none",
      zIndex: 15,
      whiteSpace: "nowrap",
      background: "linear-gradient(135deg, #212529 0%, #495057 100%)",
      color: "white",
      padding: "8px 12px",
      borderRadius: "8px",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.3)",
      border: "2px solid #ffc107",
      transform: "translateY(-5px)",
      animation: "tooltipPulse 0.3s ease-out",
    };
  }, [dragSelection]);

  // FIXED: Timeline item click handler - enhanced version
  const handleTimelineItemClick = useCallback(
    (itemId, e, time) => {
      // Prevent drag selection when clicking on timeline items
      if (dragSelection.isDragging) return;

      // Find the booking data from timeline items
      const timelineItem = memoizedTimelineItems.find(
        (item) => item.id.toString() === itemId.toString()
      );

      if (timelineItem && timelineItem.bookingData) {
        // Get the target element's bounding rect for positioning
        // Use the actual clicked element or fallback to the event target
        const targetElement = e.currentTarget || e.target;
        const rect = targetElement.getBoundingClientRect();

        // Show booking details popup
        handleBookingClick(timelineItem.bookingData, rect);
      } else {
        console.warn("⚠️ No booking data found for item:", itemId);
      }
    },
    [dragSelection.isDragging, memoizedTimelineItems, handleBookingClick]
  );

  // Memoized clear filters function
  const clearAllFilters = useCallback(() => {
    setFilterRoomType("");
    setFilteroccupancy("");
    setfilterStatus("");
  }, []);

  // Memoized date navigation functions
  const handleYearChange = useCallback(
    (e) => {
      const newYear = Number.parseInt(e.target.value);
      setSelectedYear(newYear);
      const newDate = moment([newYear, selectedMonth]).startOf("month");
      setSelectedDate(newDate);
    },
    [selectedMonth]
  );

  const handleMonthChange = useCallback(
    (monthIndex) => {
      const newDate = moment([selectedYear, monthIndex]).startOf("month");
      setSelectedMonth(monthIndex);
      setSelectedDate(newDate);
    },
    [selectedYear]
  );

  const goToToday = useCallback(() => {
    const today = moment();
    setSelectedYear(today.year());
    setSelectedMonth(today.month());
    setSelectedDate(today.startOf("month"));
  }, []);

  return (
    <div className="frontdesk-dashboard-page">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0 bg-gradient">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h1 className="card-title mb-2 text-primary d-flex align-items-center">
                    <i className="bi bi-building me-3 fs-2"></i>
                    <div>
                      <div className="fs-2 fw-bold">Front Desk Dashboard</div>
                    </div>
                  </h1>
                </div>

                {/* Action Buttons */}
                <div className="d-flex gap-2 flex-wrap">
                  <div className="position-relative">
                    <Popover.Root
                      open={showFilters}
                      onOpenChange={setShowFilters}
                    >
                      <Popover.Trigger asChild>
                        <button
                          className={`btn btn-md ${
                            getActiveFiltersCount() > 0
                              ? "btn-primary"
                              : "btn-outline-primary"
                          } position-relative shadow-sm`}
                        >
                          <i className="bi bi-funnel me-2"></i>
                          Filters
                          {getActiveFiltersCount() > 0 && (
                            <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                              {getActiveFiltersCount()}
                            </span>
                          )}
                        </button>
                      </Popover.Trigger>

                      <Popover.Portal>
                        <Popover.Content
                          side="bottom"
                          align="end"
                          sideOffset={8}
                          className="bg-white rounded-4 shadow-lg p-4 mt-2"
                          style={{ zIndex: 4000, minWidth: "380px" }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-4">
                            <h5 className="mb-0 text-primary d-flex align-items-center">
                              <i className="bi bi-sliders me-2"></i>
                              Filter Options
                            </h5>
                            <Popover.Close
                              className="btn-close"
                              aria-label="Close"
                            />
                          </div>

                          <div className="mb-3">
                            <FormSelect
                              label="Room Type"
                              value={filterRoomType}
                              onChange={(e) =>
                                setFilterRoomType(e.target.value)
                              }
                              options={roomTypesName}
                              name="roomType"
                            />
                          </div>

                          <div className="mb-3">
                            <FormSelect
                              label="Occupancy"
                              value={filteroccupancy}
                              onChange={(e) =>
                                setFilteroccupancy(e.target.value)
                              }
                              options={occupancyOptions}
                              name="occupancy"
                              placeholder="Select occupancy"
                              selectClassName="w-100"
                            />
                          </div>

                          <div className="mb-4">
                            <FormSelect
                              label="Booking Status"
                              value={filterStatus}
                              onChange={(e) => setfilterStatus(e.target.value)}
                              options={bookingStatus}
                              name="status"
                              placeholder="Select status"
                              selectClassName="w-100"
                            />
                          </div>

                          <div className="d-flex justify-content-between">
                            <button
                              className="btn btn-outline-danger"
                              onClick={clearAllFilters}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Clear All
                            </button>
                            <Popover.Close asChild>
                              <button className="btn btn-primary">
                                <i className="bi bi-check-circle me-1"></i>
                                Apply Filters
                              </button>
                            </Popover.Close>
                          </div>
                        </Popover.Content>
                      </Popover.Portal>
                    </Popover.Root>
                  </div>

                  <button
                    className="btn btn-outline-secondary btn-md shadow-sm"
                    onClick={goToToday}
                  >
                    <i className="bi bi-calendar-today me-2"></i>
                    Today
                  </button>

                  {hasAddAccess(permissions, "front_desk") && (
                    <button
                      className="btn btn-success btn-md shadow-sm"
                      onClick={() => {
                        navigate("/reservation/add-room-booking");
                      }}
                    >
                      <i className="bi bi-plus-lg me-2"></i>
                      Add Booking
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Date Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="d-flex align-items-center gap-3">
              <FormSelect
                label="Year"
                value={selectedYear}
                onChange={handleYearChange}
                options={yearOptions}
                name="year"
                groupClassName="d-flex align-items-center gap-2"
                labelClassName="text-nowrap mb-0 fw-semibold"
              />
            </div>

            <div className="d-flex flex-wrap gap-2 align-items-center">
              <span className="text-muted fw-semibold me-2">Month:</span>
              {months.map((month, i) => (
                <button
                  key={month}
                  onClick={() => handleMonthChange(i)}
                  className={`btn ${
                    i === selectedMonth
                      ? "btn-primary shadow-sm"
                      : "btn-outline-secondary"
                  }`}
                >
                  {month.substring(0, 3)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0 d-flex align-items-center">
                <i className="bi bi-calendar3 me-2"></i>
                Room Availability Timeline
                {dragSelection.isDragging && dragSelection.isActive && (
                  <span className="badge bg-warning text-dark ms-auto animate-pulse">
                    <i className="bi bi-arrows-move me-1"></i>
                    Selecting: {dragSelection.startDate?.format(
                      "MMM DD"
                    )} - {dragSelection.endDate?.format("MMM DD")}(
                    {Math.abs(
                      dragSelection.endDate?.diff(
                        dragSelection.startDate,
                        "days"
                      ) || 0
                    )}{" "}
                    nights)
                  </span>
                )}
              </h5>
            </div>
            <div className="card-body p-0">
              {memoizedTimelineGroups.length > 0 ? (
                <div
                  ref={timelineContainerRef}
                  className="timeline-container position-relative"
                  style={{
                    cursor: dragSelection.isDragging ? "grabbing" : "crosshair",
                    userSelect: "none",
                  }}
                  onMouseDown={handleTimelineMouseDown}
                  onMouseMove={
                    dragSelection.isDragging
                      ? handleTimelineMouseMove
                      : undefined
                  }
                  onMouseUp={
                    dragSelection.isDragging ? handleTimelineMouseUp : undefined
                  }
                >
                  <Timeline
                    key={`${filterRoomType}-${filteroccupancy}-${filterStatus}-${selectedYear}-${selectedMonth}`}
                    groups={memoizedTimelineGroups}
                    items={memoizedTimelineItems}
                    visibleTimeStart={visibleTimeStart}
                    visibleTimeEnd={visibleTimeEnd}
                    onTimeChange={(start, end, updateScrollCanvas) => {
                      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
                    }}
                    minZoom={24 * 60 * 60 * 1000}
                    maxZoom={30 * 24 * 60 * 60 * 1000}
                    timeSteps={{ day: 1 }}
                    canMove={false}
                    canResize={false}
                    canSelect={false}
                    stackItems
                    itemHeightRatio={0.8}
                    lineHeight={55}
                    sidebarWidth={timelineDimensions.sidebarWidth}
                    headerLabelFormats={{
                      dayShort: "D (ddd)",
                    }}
                    groupRenderer={({ group }) => {
                      const isChild = !!group.parent;
                      const isRoomType = group.isRoomType;
                      const isDisabled = group.isDisabled;

                      return (
                        <div
                          data-group-id={group.id}
                          className={`p-3 ${isChild ? "ps-4" : ""} ${
                            isChild
                              ? "border-start border-primary border-3"
                              : ""
                          } ${
                            isRoomType || isDisabled
                              ? "room-type-disabled non-selectable-row disabled-row"
                              : ""
                          }`}
                          style={{
                            fontWeight: isChild ? "600" : "bold",
                            fontSize: isChild ? "14px" : "16px",
                            color:
                              isRoomType || isDisabled
                                ? "#6c757d"
                                : isChild
                                ? "#495057"
                                : "#212529",
                            backgroundColor:
                              isRoomType || isDisabled
                                ? "#f8f9fa"
                                : isChild
                                ? "#ffffff"
                                : "#e9ecef",
                            borderBottom: "1px solid #dee2e6",
                            cursor:
                              isRoomType || isDisabled
                                ? "not-allowed"
                                : isChild
                                ? "crosshair"
                                : "default",
                            userSelect: "none",
                            transition: "background-color 0.2s ease",
                            position: "relative",
                            opacity: isRoomType || isDisabled ? 0.5 : 1,
                            pointerEvents:
                              isRoomType || isDisabled ? "none" : "auto",
                            borderLeft:
                              isRoomType || isDisabled
                                ? "5px solid #dc3545"
                                : undefined,
                            filter:
                              isRoomType || isDisabled
                                ? "grayscale(70%)"
                                : "none",
                            background:
                              isRoomType || isDisabled
                                ? "repeating-linear-gradient(45deg, #f8f9fa, #f8f9fa 10px, #e9ecef 10px, #e9ecef 20px)"
                                : undefined,
                          }}
                          onMouseDown={(e) => {
                            if (isRoomType || isDisabled) {
                              e.preventDefault();
                              e.stopPropagation();
                              return false;
                            }
                          }}
                          onClick={(e) => {
                            if (isRoomType || isDisabled) {
                              e.preventDefault();
                              e.stopPropagation();
                              return false;
                            }
                          }}
                          onDragStart={(e) => {
                            if (isRoomType || isDisabled) {
                              e.preventDefault();
                              return false;
                            }
                          }}
                        >
                          {isRoomType && <i className="bi "></i>}
                          {group.title}
                          {isRoomType && (
                            <small className="text-danger ms-2 fw-bold"></small>
                          )}
                        </div>
                      );
                    }}
                    onItemClick={handleTimelineItemClick}
                    onItemSelect={handleTimelineItemClick}
                    onItemDoubleClick={handleTimelineItemClick}
                  />

                  {/* Enhanced Drag Selection Overlay */}
                  <div style={getSelectionOverlayStyle()} />

                  {/* Enhanced Selection Tooltip */}
                  <div style={getSelectionTooltipStyle()}>
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-calendar-range-fill text-warning"></i>
                      <span>
                        {dragSelection.startDate?.format("MMM DD")} -{" "}
                        {dragSelection.endDate?.format("MMM DD")}
                      </span>
                      <span className="badge bg-warning text-dark">
                        {Math.abs(
                          dragSelection.endDate?.diff(
                            dragSelection.startDate,
                            "days"
                          ) || 0
                        )}{" "}
                        nights
                      </span>
                    </div>
                    <div className="text-center mt-1 text-warning">
                      <small>Room {dragSelection.roomNumber}</small>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="mb-4">
                    <i className="bi bi-search display-1 text-muted"></i>
                  </div>
                  <h4 className="text-muted mb-3">
                    No rooms match the current filters
                  </h4>
                  <p className="text-muted mb-4 lead">
                    Try adjusting your filter criteria or clear all filters to
                    see available rooms.
                  </p>
                  <button
                    className="btn btn-primary btn-lg"
                    onClick={clearAllFilters}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Date Range Modal */}
      {showDateRangeModal &&
        finalSelection.startDate &&
        finalSelection.endDate && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header bg-gradient bg-success text-white">
                  <h4 className="modal-title d-flex align-items-center">
                    <i className="bi bi-calendar-plus-fill me-3"></i>
                    Create New Reservation
                  </h4>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setShowDateRangeModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="row g-4">
                    <div className="col-12">
                      <div className="alert alert-light border-2 border-success">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-house-door-fill text-success me-3 fs-4"></i>
                          <div>
                            <h6 className="mb-0">Selected Room</h6>
                            <strong className="text-success fs-5">
                              Room {finalSelection.roomNumber}
                            </strong>
                            <div className="text-muted">
                              {finalSelection.roomType}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold fs-6">
                        <i className="bi bi-calendar-check me-2 text-success"></i>
                        Check-in Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-lg"
                        value={finalSelection.startDate.format("YYYY-MM-DD")}
                        onChange={(e) =>
                          setFinalSelection((prev) => ({
                            ...prev,
                            startDate: moment(e.target.value),
                            duration: prev.endDate.diff(
                              moment(e.target.value),
                              "days"
                            ),
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold fs-6">
                        <i className="bi bi-calendar-x me-2 text-danger"></i>
                        Check-out Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-lg"
                        value={finalSelection.endDate.format("YYYY-MM-DD")}
                        onChange={(e) =>
                          setFinalSelection((prev) => ({
                            ...prev,
                            endDate: moment(e.target.value),
                            duration: moment(e.target.value).diff(
                              prev.startDate,
                              "days"
                            ),
                          }))
                        }
                        min={finalSelection.startDate.format("YYYY-MM-DD")}
                      />
                    </div>
                    <div className="col-12">
                      <div className="alert alert-success border-0 bg-gradient">
                        <div className="row text-center">
                          <div className="col-md-4">
                            <i className="bi bi-clock-fill fs-3 text-success d-block mb-2"></i>
                            <h6 className="mb-0">Duration</h6>
                            <strong className="fs-5">
                              {finalSelection.duration} nights
                            </strong>
                          </div>
                          <div className="col-md-4">
                            <i className="bi bi-calendar-range-fill fs-3 text-info d-block mb-2"></i>
                            <h6 className="mb-0">Check-in</h6>
                            <strong className="fs-6">
                              {finalSelection.startDate.format("MMM DD, YYYY")}
                            </strong>
                          </div>
                          <div className="col-md-4">
                            <i className="bi bi-calendar-x-fill fs-3 text-warning d-block mb-2"></i>
                            <h6 className="mb-0">Check-out</h6>
                            <strong className="fs-6">
                              {finalSelection.endDate.format("MMM DD, YYYY")}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer p-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-lg"
                    onClick={() => setShowDateRangeModal(false)}
                  >
                    <i className="bi bi-x-circle me-2"></i>
                    Cancel
                  </button>
                  {hasAddAccess(permissions, "reservation") && (
                    <button
                      type="button"
                      className="btn btn-success btn-lg"
                      onClick={navigateToAddReservation}
                      disabled={
                        !finalSelection.startDate || !finalSelection.endDate
                      }
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Create Reservation
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      {/* UPDATED: Enhanced Booking Details Popup with Multi-Room Support */}
      {selectedBooking && (
        <div
          ref={dropdownRef}
          className="position-fixed bg-white border-0 rounded-3 shadow-lg p-3"
          style={{
            top: dropdownPosition.top - 300,
            left: dropdownPosition.left,
            bottom: dropdownPosition.bottom,
            zIndex: 1050,
            width: "350px",
            maxWidth: "90vw",
            maxHeight: dropdownPosition.maxHeight || "80vh",
            overflowY: "auto",
            fontSize: "0.875rem",
            boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <div className="mb-2 border-bottom pb-2">
            <h6 className="mb-0 d-flex align-items-center gap-2 justify-content-between text-primary">
              <div className=" d-flex align-items-center gap-2">
                <i className="bi bi-calendar-check-fill fs-5"></i>
                Booking Details
                {selectedBooking.rooms && selectedBooking.rooms.length > 1 && (
                  <span className="badge bg-info text-white">
                    {selectedBooking.rooms.length} Rooms
                  </span>
                )}
              </div>
              <div>
                <i
                  className="bi bi-x-lg me-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelectedBooking(null)}
                ></i>
              </div>
            </h6>
          </div>

          <div className="row g-2">
            <div className="col-12">
              <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                <i className="bi bi-person-circle fs-4 text-primary"></i>
                <div>
                  <div className="mb-0 small">Guest Name</div>
                  <strong className="fs-6">
                    {selectedBooking.guest.first_name}{" "}
                    {selectedBooking.guest.last_name}
                  </strong>
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between small">
                <span className="fw-semibold">Booking ID:</span>
                <span className="badge bg-secondary">
                  {selectedBooking.booking_id}
                </span>
              </div>
            </div>

            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between small">
                <span className="fw-semibold">Status:</span>
                <span
                  className={`badge fs-6 ${
                    selectedBooking.booking_status === "confirmed"
                      ? "bg-success"
                      : selectedBooking.booking_status === "booked"
                      ? "bg-primary"
                      : selectedBooking.booking_status === "check_in"
                      ? "bg-info"
                      : "bg-warning text-dark"
                  }`}
                >
                  {selectedBooking.booking_status.replace("_", " ")}
                </span>
              </div>
            </div>

            {/* UPDATED: Display multiple rooms */}
            {selectedBooking.rooms && selectedBooking.rooms.length > 0 && (
              <div className="col-12">
                <div className="p-2 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-2 fs-6 d-flex align-items-center">
                      <i className="bi bi-house-door me-2"></i>
                      Room{selectedBooking.rooms.length > 1 ? "s" : ""}
                      <span className="badge bg-primary ms-2">
                        {selectedBooking.rooms.length}
                      </span>
                    </h6>
                    {hasEditAccess(permissions, "reservation") && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => navigate('/reservation/booking-list?reassign=true',{state:selectedBooking})}
                      >
                        <UserCheck size={14} className="me-2" />
                        Reassign
                      </button>
                    )}
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {selectedBooking.rooms.map((room, index) => (
                      <span
                        key={index}
                        className="badge bg-outline-primary border border-primary text-primary"
                        style={{ fontSize: "0.75rem" }}
                      >
                        Room{" "}
                        {rooms?.data
                          ?.find((rt) =>
                            rt.room?.find((r) => r.id === room.room_id)
                          )
                          ?.room?.find((r) => r.id === room.room_id)
                          ?.room_number || room.room_id}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Services */}
            {selectedBooking.services &&
              selectedBooking.services.length > 0 && (
                <div className="col-12">
                  <div className="p-2 bg-light rounded">
                    <h6 className="mb-2 fs-6 d-flex align-items-center">
                      <i className="bi bi-gear me-2"></i>
                      Services
                    </h6>
                    <div className="d-flex flex-wrap gap-1">
                      {selectedBooking.services.map((service, index) => (
                        <div key={index} className="small">
                          <span className="badge bg-success">
                            {service.package_name || "Service"}
                          </span>
                          {service.package_price && (
                            <span className="text-muted ms-1">
                              ₹{service.package_price}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            <div className="col-6">
              <div className="text-center p-2 bg-success bg-opacity-10 rounded">
                <i className="bi bi-box-arrow-in-right text-success fs-5 d-block mb-1"></i>
                <small className="text-muted">Check In</small>
                <div className="fw-bold small">
                  {moment(selectedBooking.check_in_date_time).format(
                    "MMM DD, YYYY"
                  )}
                </div>
                <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                  {moment(selectedBooking.check_in_date_time).format("HH:mm")}
                </div>
              </div>
            </div>

            <div className="col-6">
              <div className="text-center p-2 bg-danger bg-opacity-10 rounded">
                <i className="bi bi-box-arrow-left text-danger fs-5 d-block mb-1"></i>
                <small className="text-muted">Check Out</small>
                <div className="fw-bold small">
                  {moment(selectedBooking.check_out_date_time).format(
                    "MMM DD, YYYY"
                  )}
                </div>
                <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                  {moment(selectedBooking.check_out_date_time).format("HH:mm")}
                </div>
              </div>
            </div>

            {/* Total Amount */}
            {selectedBooking.total_amount &&
              selectedBooking.total_amount !== "0.00" && (
                <div className="col-12">
                  <div className="text-center p-2 bg-warning bg-opacity-10 rounded">
                    <i className="bi bi-currency-dollar text-warning fs-5 d-block mb-1"></i>
                    <small className="text-muted">Total Amount</small>
                    <div className="fw-bold">
                      ₹{selectedBooking.total_amount}
                    </div>
                  </div>
                </div>
              )}
          </div>

          <div className="d-flex justify-content-between mt-3 pt-2 border-top">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() =>
                navigate(`/reservation/add-room-booking/${selectedBooking.id}`)
              }
            >
              <i className="bi bi-eye-fill me-1"></i>
              View
            </button>
            
            {selectedBooking?.booking_status === "check_in" && <button
              className="btn btn-sm btn-outline-danger"
              onClick={() =>
                navigate(`/reservation/check-out-booking/${selectedBooking.id}`)
              }
            >
              <i className="bi bi-box-arrow-left me-1"></i>
              Check Out
            </button>}
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setSelectedBooking(null)}
            >
              <i className="bi bi-x-circle me-1"></i>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Enhanced Room Details Popup with Smart Positioning */}
      {openDialog && roomDetail && (
        <div
          ref={roomPopupRef}
          className="position-fixed bg-white border-0 rounded-3 shadow-lg p-3"
          style={{
            top: roomPopupPos.y,
            left: roomPopupPos.x,
            zIndex: 1050,
            bottom: "50px",
            width: roomPopupPos.width || 320,
            maxWidth: "90vw",
            maxHeight: roomPopupPos.maxHeight || "80vh",
            overflowY: "auto",
            boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
            fontSize: "0.875rem",
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
            <h6 className="mb-0 fw-bold text-primary d-flex align-items-center">
              <i className="bi bi-house-door-fill me-2 fs-5"></i>
              Room {roomDetail?.data?.room_number}
            </h6>
            <button
              type="button"
              className="btn-close btn-sm"
              onClick={() => setOpenDialog(false)}
            ></button>
          </div>

          <div className="mb-2">
            <small className="text-muted">
              Floor {roomDetail?.data?.floor_number}
            </small>
          </div>

          <div className="mb-3">
            {/* <span
              className={`badge me-2 fs-6 ${
                roomDetail?.data?.room_status === "available" ? "bg-success" : "bg-secondary"
              }`}
            >
              {roomDetail?.data?.room_status}
            </span> */}
            <span className="badge bg-info fs-6">
              {roomDetail?.data?.room?.room_type}
            </span>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-6">
              <div className="text-center p-2 bg-light rounded">
                <i className="bi bi-people-fill text-primary fs-4 d-block mb-1"></i>
                <small className="text-muted">Capacity</small>
                <div className="fw-bold">
                  {roomDetail?.data?.room?.capacity}
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="text-center p-2 bg-light rounded">
                <i className="bi bi-currency-dollar text-success fs-4 d-block mb-1"></i>
                <small className="text-muted">Price/night</small>
                <div className="fw-bold">₹{roomDetail?.data?.room?.price}</div>
              </div>
            </div>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-12">
              <div className="p-2 bg-light rounded">
                <h6 className="mb-1 fs-6">
                  <i className="bi bi-bed me-2"></i>
                  Bed Configuration
                </h6>
                <div>
                  {roomDetail?.data?.room?.bed_count} x{" "}
                  {roomDetail?.data?.room?.bed_type}
                </div>
              </div>
            </div>
            <div className="col-12">
              <div className="p-2 bg-light rounded">
                <h6 className="mb-1 fs-6">
                  <i className="bi bi-rulers me-2"></i>
                  Room Size
                </h6>
                <div>{roomDetail?.data?.room?.room_size} m²</div>
              </div>
            </div>
          </div>

          {roomDetail?.data?.room?.extra_bed !== "0.00" && (
            <div className="mb-3">
              <div className="alert alert-info py-2 px-3 mb-0">
                <i className="bi bi-plus-circle me-2"></i>
                <strong>Extra Bed:</strong> ₹{roomDetail?.data?.room?.extra_bed}
              </div>
            </div>
          )}

          <div className="text-end">
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                const reservationData = {
                  roomId: roomDetail?.data?.id,
                  roomNumber: roomDetail?.data?.room_number,
                  roomType: roomDetail?.data?.room_type,
                  checkIn: "",
                  checkOut: "",
                };

                navigate("/reservation/add-room-booking", {
                  state: reservationData,
                });
              }}
            >
              <i className="bi bi-calendar-plus me-1"></i>
              Book
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .timeline-container .react-calendar-timeline .rct-sidebar {
          background: linear-gradient(
            135deg,
            #f8f9fa 0%,
            #e9ecef 100%
          ) !important;
          border-right: 2px solid #007bff !important;
        }

        .timeline-container .react-calendar-timeline .rct-header {
          background: linear-gradient(
            135deg,
            #ffffff 0%,
            #f8f9fa 100%
          ) !important;
          border-bottom: 3px solid #007bff !important;
        }

        .timeline-container .react-calendar-timeline .rct-dateHeader {
          color: #495057 !important;
          font-weight: 700 !important;
          font-size: 14px !important;
        }

        .timeline-container .react-calendar-timeline .rct-item {
          border-radius: 10px !important;
          border: 2px solid rgba(0, 0, 0, 0.1) !important;
          transition: all 0.3s ease !important;
        }

        .timeline-container .react-calendar-timeline .rct-item:hover {
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2) !important;
          transform: translateY(-2px) !important;
          border-color: #007bff !important;
        }

        /* Multi-room reservation styling */
        .timeline-container
          .react-calendar-timeline
          .rct-item[style*="border: 2px solid #007bff"] {
          background: linear-gradient(
            135deg,
            rgba(0, 123, 255, 0.1) 0%,
            rgba(0, 123, 255, 0.2) 100%
          ) !important;
          border-left: 4px solid #007bff !important;
        }

        .timeline-container
          .react-calendar-timeline
          .rct-item[style*="border: 2px solid #007bff"]:before {
          content: "🏠";
          position: absolute;
          top: 2px;
          right: 2px;
          font-size: 10px;
          opacity: 0.7;
        }

        .card {
          transition: all 0.3s ease;
        }

        .card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
        }

        .btn {
          transition: all 0.3s ease;
        }

        .btn:hover {
          transform: translateY(-1px);
        }

        .modal {
          backdrop-filter: blur(8px);
        }

        .bg-gradient {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        }

        .timeline-container {
          user-select: none;
        }

        .timeline-container .react-calendar-timeline .rct-canvas {
          transition: cursor 0.2s ease;
        }

        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes tooltipPulse {
          0% {
            transform: translateY(-5px) scale(0.9);
            opacity: 0;
          }
          100% {
            transform: translateY(-5px) scale(1);
            opacity: 1;
          }
        }

        .timeline-container .react-calendar-timeline .rct-canvas:hover {
          cursor: crosshair;
        }

        .timeline-container.dragging .react-calendar-timeline .rct-canvas {
          cursor: grabbing !important;
        }

        /* Enhanced room number clickability */
        .room-clickable {
          position: relative !important;
          z-index: 1000 !important;
          pointer-events: auto !important;
          cursor: pointer !important;
          padding: 2px 4px !important;
          border-radius: 4px !important;
          transition: all 0.2s ease !important;
        }

        .room-clickable:hover {
          background-color: rgba(0, 123, 255, 0.1) !important;
          transform: scale(1.05) !important;
          box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2) !important;
        }

        .room-clickable:active {
          transform: scale(0.98) !important;
          background-color: rgba(0, 123, 255, 0.2) !important;
        }

        /* FIXED: Room type row styling - completely disabled and visually distinct */
        .room-type-disabled,
        .non-selectable-row,
        .disabled-row {
          cursor: not-allowed !important;
          pointer-events: none !important;
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
        }

        .room-type-disabled *,
        .non-selectable-row *,
        .disabled-row * {
          pointer-events: none !important;
          cursor: not-allowed !important;
          user-select: none !important;
        }

        .room-type-disabled:hover,
        .non-selectable-row:hover,
        .disabled-row:hover {
          background: inherit !important;
          cursor: not-allowed !important;
          transform: none !important;
        }

        /* Ensure only room number rows are interactive */
        .timeline-container
          .react-calendar-timeline
          .rct-sidebar-row:not(.room-type-disabled):not(.disabled-row) {
          cursor: crosshair;
        }

        .timeline-container
          .react-calendar-timeline
          .rct-sidebar-row:not(.room-type-disabled):not(.disabled-row):hover {
          background-color: rgba(0, 123, 255, 0.05) !important;
        }

        /* Enhanced popup animations */
        .position-fixed {
          animation: popupFadeIn 0.2s ease-out;
        }

        @keyframes popupFadeIn {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        /* Responsive popup adjustments */
        @media (max-width: 768px) {
          .position-fixed {
            width: 90vw !important;
            max-width: 90vw !important;
            left: 5vw !important;
            right: 5vw !important;
          }
        }

        /* Smooth scrolling for popup content */
        .position-fixed {
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 123, 255, 0.3) transparent;
        }

        .position-fixed::-webkit-scrollbar {
          width: 6px;
        }

        .position-fixed::-webkit-scrollbar-track {
          background: transparent;
        }

        .position-fixed::-webkit-scrollbar-thumb {
          background-color: rgba(0, 123, 255, 0.3);
          border-radius: 3px;
        }

        .position-fixed::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 123, 255, 0.5);
        }
      `}</style>
    </div>
  );
});

export default FrontDeskDashboard;
